import React, { useEffect } from 'react';
import { useMetadata } from '../contexts/MetadataContext';
import { useSubdomain } from '../hooks/useSubdomain';

function SEO({ 
  title, 
  description, 
  keywords,
  image,
  type,
  structuredData
}) {
  const baseMetadata = useMetadata();
  const { subdomain } = useSubdomain();
  const assetBaseUrl = import.meta.env.VITE_ASSET_BASE_URL || 'https://assets.therize.net';
  
  // Use provided values or fall back to base metadata
  const pageTitle = title || baseMetadata.title;
  const pageDescription = description || baseMetadata.description;
  const pageKeywords = keywords || baseMetadata.keywords;
  const pageImage = image 
    ? `${assetBaseUrl}/subdomains/${subdomain}/${image}`
    : `${assetBaseUrl}/subdomains/${subdomain}/${baseMetadata.ogImage}`;
  const pageType = type || baseMetadata.ogType || 'website';
  
  // Merge structured data
  const pageStructuredData = {
    ...baseMetadata.structuredData,
    ...structuredData,
    name: pageTitle,
    description: pageDescription
  };
  
  useEffect(() => {
    // Update document title
    document.title = pageTitle;
    
    // Update meta tags
    const metaTags = {
      'description': pageDescription,
      'keywords': pageKeywords,
      'og:title': pageTitle,
      'og:description': pageDescription,
      'og:image': pageImage,
      'og:type': pageType,
      'twitter:title': pageTitle,
      'twitter:description': pageDescription,
      'twitter:image': pageImage
    };
    
    // Update each meta tag
    Object.entries(metaTags).forEach(([name, content]) => {
      // Handle both name and property attributes
      const selector = `meta[name="${name}"], meta[property="${name}"]`;
      const element = document.querySelector(selector);
      
      if (element) {
        element.setAttribute(
          element.hasAttribute('name') ? 'content' : 'content', 
          content
        );
      }
    });
    
    // Update structured data
    const ldScript = document.querySelector('script[type="application/ld+json"]');
    if (ldScript) {
      ldScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        ...pageStructuredData
      }, null, 2);
    }
    
    // Clean up not needed as these changes persist
  }, [pageTitle, pageDescription, pageKeywords, pageImage, pageType, pageStructuredData]);
  
  return null;
}

export default SEO;
